import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPencil, FaPlus } from "react-icons/fa6";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { useCallback, useEffect, useState } from "react";
import { LinkedDevice, UnlinkedDevice } from "@/api/devices";
import { api } from "@/api";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useDisclosure } from "@heroui/react";
import { CreateDeviceModal } from "@/app/components/modals/device/createDeviceModal";
import { EditDeviceModal } from "@/app/components/modals/device/editDeviceModal";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";
import { useLocation } from "react-router-dom";

export function SkudDevicesPage() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);

  const [devices, setDevices] = useState<LinkedDevice[] | null>(null);
  const [unlinkedDevices, setUnlinkedDevices] = useState<
    UnlinkedDevice[] | null
  >(null);

  const [linkedDevice, setLinkedDevice] = useState<LinkedDevice | null>(null);
  const [unlinkedDevice, setUnlinkedDevice] = useState<UnlinkedDevice | null>(
    null,
  );

  const editDeviceDisclosure = useDisclosure();
  const createDeviceDisclosure = useDisclosure();

  const handleError = useErrorHandling();

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  useEffect(() => {
    api.devices
      .list()
      .then((response) => {
        setDevices(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });

    api.devices
      .getUnlinked()
      .then((response) => {
        setUnlinkedDevices(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [editDeviceDisclosure.isOpen, createDeviceDisclosure.isOpen, handleError]);

  const createDevice = useCallback(
    (device: UnlinkedDevice) => {
      setUnlinkedDevice(device);
      createDeviceDisclosure.onOpenChange();
    },
    [createDeviceDisclosure],
  );

  const editDevice = useCallback(
    (device: LinkedDevice) => {
      setLinkedDevice(device);
      editDeviceDisclosure.onOpenChange();
    },
    [editDeviceDisclosure],
  );

  return (
    <>
      <CreateDeviceModal
        disclosure={createDeviceDisclosure}
        device={unlinkedDevice}
      />
      <EditDeviceModal
        disclosure={editDeviceDisclosure}
        device={linkedDevice}
      />
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 md:min-w-[240px] w-full md:w-auto">
          {/* @ts-ignore */}
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow flex flex-col gap-4">
          <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
            <div className="flex flex-row justify-between w-full items-center mb-4">
              <div className="flex flex-row gap-4 items-center">
                <span className="font-medium">Привязанные устройства</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 mb-2">
              <TableBuilder
                displayOptions={false}
                removeWrapper={true}
                columns={[
                  {
                    label: "ID устройства",
                    key: "device_id",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Название",
                    key: "name",
                    type: ColumnType.String,
                    sortable: true,
                  },
                  {
                    label: "Локация",
                    key: "location_id",
                    type: ColumnType.Location,
                    sortable: true,
                  },
                  {
                    label: "Этаж",
                    key: "floor",
                    type: ColumnType.Number,
                    sortable: true,
                  },
                  {
                    key: "actions",
                    label: "Действия",
                    type: ColumnType.Actions,
                    actions: [
                      {
                        icon: <FaPencil />,
                        onClick: (_value, row) => {
                          editDevice(row);
                        },
                      },
                    ],
                  },
                ]}
                data={devices || []}
                rowsPerPage={10}
                defaultSortDescriptor={{
                  column: "name",
                  direction: "ascending",
                }}
              />
            </div>
            {(unlinkedDevices || []).length > 0 && (
              <>
                <span className="font-medium">
                  Обнаружены непривязанные устройства:
                </span>
                <div className="flex flex-col gap-2 mb-2 mt-2">
                  <TableBuilder
                    displayOptions={false}
                    removeWrapper={true}
                    displaySearch={false}
                    columns={[
                      {
                        label: "ID устройства",
                        key: "id",
                        type: ColumnType.String,
                        sortable: true,
                      },
                      {
                        label: "Название",
                        key: "name",
                        type: ColumnType.String,
                        sortable: true,
                      },
                      {
                        label: "Локация",
                        key: "location",
                        type: ColumnType.String,
                        sortable: true,
                      },
                      {
                        label: "Статус",
                        key: "status",
                        type: ColumnType.String,
                        sortable: true,
                      },
                      {
                        label: "Последняя активность",
                        key: "last_seen",
                        type: ColumnType.DateTime,
                        sortable: true,
                      },
                      {
                        key: "actions",
                        label: "Действия",
                        type: ColumnType.Actions,
                        actions: [
                          {
                            icon: <FaPlus />,
                            onClick: (_value, row) => {
                              createDevice(row);
                            },
                          },
                        ],
                      },
                    ]}
                    data={unlinkedDevices || []}
                    rowsPerPage={10}
                    defaultSortDescriptor={{
                      column: "name",
                      direction: "ascending",
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
